"use client";

import React from "react";
import { motion } from "framer-motion";
import { Globe, ShoppingBag, Briefcase, Building2, UserCheck, ArrowUpRight, AlertCircle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const audiences = [{
  icon: ShoppingBag,
  title: "UK Ecommerce Brands Shipping Overseas",
  badgeLabel: "Ecommerce",
  description: <>
        Retailers and consumer brands expanding shipping into Europe, North America, or Australasia. International{" "}
        <Link to="/EcommerceSeoPage" className="font-medium text-primary underline underline-offset-4 decoration-primary/40 hover:decoration-primary transition-colors inline-flex items-center gap-0.5">
          Ecommerce SEO
          <ArrowUpRight className="size-3.5 inline shrink-0" />
        </Link>{" "}
        aligns currencies, tax information, localised shipping policies, and regional product indexation to avoid duplicate content across localized storefronts.
      </>
}, {
  icon: Briefcase,
  title: "UK B2B & Professional Services",
  badgeLabel: "B2B & Services",
  description: <>
        Consultancies, software providers, and specialist service firms targeting business clients across Europe, the Middle East, or the US. Tailored{" "}
        <Link to="/B2BSeoLeadGenerationPage" className="font-medium text-primary underline underline-offset-4 decoration-primary/40 hover:decoration-primary transition-colors inline-flex items-center gap-0.5">
          B2B SEO lead generation
          <ArrowUpRight className="size-3.5 inline shrink-0" />
        </Link>{" "}
        maps multi-stakeholder research journeys, regional regulations, and country-specific enquiry pathways.
      </>
}, {
  icon: Building2,
  title: "International Companies Coordinating Global SEO",
  badgeLabel: "Global Brands",
  description: <>
        Multinational businesses that want a UK-based consultant to coordinate technical strategy, hreflang deployments, and cross-market content standards from a convenient European time zone, aligning multiple regional websites into a unified search strategy.
      </>
}, {
  icon: Globe,
  title: "Marketing Leaders Comparing Expansion Options",
  badgeLabel: "Strategic Planning",
  description: <>
        Marketing directors and business owners evaluating overseas viability before committing large budgets. If you are preparing to{" "}
        <Link to="/HireSeoConsultantPage" className="font-medium text-primary underline underline-offset-4 decoration-primary/40 hover:decoration-primary transition-colors inline-flex items-center gap-0.5">
          hire an SEO consultant
          <ArrowUpRight className="size-3.5 inline shrink-0" />
        </Link>
        , an upfront international feasibility review clarifies search demand, local competition, and realistic unit economics.
      </>
}];
const containerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.12
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 18
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: [0.21, 0.47, 0.32, 0.98] as const
    }
  }
};
export default function InternationalAudience() {
  return <section id="whoneedsinternationalseo" data-nav="light" className="relative bg-background text-foreground py-20 md:py-28 overflow-hidden border-b border-border/60">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5
      }} className="max-w-3xl mb-14 md:mb-16">
          <Badge variant="secondary" className="mb-4 text-xs font-semibold uppercase tracking-wider text-primary border-border/80 bg-secondary px-3 py-1">
            Target Audiences &amp; Fit
          </Badge>

          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-foreground font-default leading-[1.15] mb-5">
            Who Needs an International SEO Consultant?
          </h2>

          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed font-default">
            Expanding into foreign markets requires deliberate technical architecture and cultural search alignment. I work with businesses where cross-border search represents a genuine, high-value commercial opportunity.
          </p>
        </motion.div>

        {/* 4-Card Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true
      }} className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 mb-12">
          {audiences.map((audience, i) => {
          const Icon = audience.icon;
          return <motion.div key={audience.title} variants={itemVariants} data-index={i}>
                <Card className="h-full bg-card border border-border shadow-xs hover:shadow-md hover:border-primary/30 transition-all duration-300 rounded-xl flex flex-col justify-between p-6 sm:p-7">
                  <div>
                    <CardHeader className="p-0 mb-5 gap-3">
                      <div className="flex items-center justify-between gap-4">
                        <div className="size-11 rounded-lg bg-secondary flex items-center justify-center text-primary shrink-0 border border-border/50">
                          <Icon className="size-5" />
                        </div>
                        <span className="text-xs font-medium text-muted-foreground font-mono">
                          {audience.badgeLabel}
                        </span>
                      </div>
                      <CardTitle className="text-xl sm:text-2xl font-semibold text-card-foreground pt-1">
                        {audience.title}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-0 text-sm sm:text-base text-muted-foreground leading-relaxed font-default">
                      {audience.description}
                    </CardContent>
                  </div>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* The Single Consultant-Involvement Block */}
        <div className="bg-card border border-border rounded-2xl p-6 sm:p-8 md:p-10 mb-8 shadow-xs">
          <div className="flex items-start gap-4">
            <div className="size-12 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0 text-primary">
              <UserCheck className="size-6" />
            </div>
            <div>
              <h3 className="text-xl sm:text-2xl font-semibold text-foreground font-default mb-2">
                How I work with you
              </h3>
              <p className="text-sm sm:text-base text-muted-foreground leading-relaxed max-w-4xl">
                I am an independent consultant who personally does the strategy, audits, and recommendations, and can work with your developers, content team, and translators on implementation. This ensures technical recommendations are delivered directly to the engineers building them, while your marketing and translation partners receive clear, search-aligned briefs.
              </p>
            </div>
          </div>
        </div>

        {/* Minimum Fit & "Not Yet" Note */}
        <div className="bg-muted/50 border border-border rounded-xl p-5 sm:p-6 flex items-start gap-3.5">
          <AlertCircle className="size-5 text-accent shrink-0 mt-0.5" />
          <div className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
            <span className="font-semibold text-foreground">A note on commercial readiness: </span>
            A successful international expansion requires an existing site with some traction, a realistic budget for one to three target markets, and the operational capacity to serve overseas customers (shipping, fulfilment, customer service, and local payment methods). Sometimes the right strategic answer is &ldquo;not yet&rdquo;: if your UK organic foundations are still weak, it is far more profitable to strengthen your domestic position first before dividing resources across borders.
          </div>
        </div>
      </div>
    </section>;
}